import { FC, useState } from "react";
import SubmitItem from "../../../components/template/SubmitItem/SubmitItem";
import { FieldValues, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { Toast } from "../../../components/atom/Toast/Toast";
import { handleSubmitItem } from "../../../helpers/handleSubmitItem";
import { useStepper } from "../../../hooks/stepper/useStepper";
import { StepperConfig } from "../../../types/Components/Stepper/StepperConfig";
import { AlertDialogConfig } from "../../../types/Components/Alert/AlertDialogConfig";
import api from "../../../utils/api";
import { FieldOccurencesItemSchema } from "../data/FieldOccurencesItemSchema";
import FieldOccurancesSubmitForm from "./FieldOccurancesSubmitForm";
import { OCCURENCE_URL } from "../../../utils/endpoints";

const NewFieldOccurrences: FC = () => {
  const { addToast } = Toast();
  const navigate = useNavigate();
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(true);

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    watch,
    trigger,
    resetField,
  } = useForm({
    resolver: zodResolver(FieldOccurencesItemSchema),
    mode: "onChange",
    shouldUseNativeValidation: false,
  });

  const onSubmit = async (data: FieldValues) => {
    const bodyRequest = {
      occurrenceTypeId: data.typeOption.id,
      occurrenceLevelId: data.levelOption.id,
      title: data.title,
      description: data.description,
      provinceId: data.provinceOption ? data.provinceOption.id : null,
      countyId: data.countyOption ? data.countyOption.id : null,
    };
    await handleSubmitItem(
      () => api.post(OCCURENCE_URL, bodyRequest),
      addToast,
      setIsLoaded,
      "Create",
      "Ocorrência criada",
    );
    navigate("/ocorrencias-no-terreno");
  };

  const steps = [
    { title: "Preencher as informações da ocorrência", description: "Passo 1" },
    {
      title: "Verificar as informações da ocorrência a emitir",
      description: "Passo 2",
    },
  ];

  const { currentStep, handleNext, handleBack, handleFinalSubmit } = useStepper(
    steps,
    trigger,
    handleSubmit,
    onSubmit,
  );

  const stepperConfig: StepperConfig = {
    nextButtonName: "Criar ocorrência",
    steps,
    onNext: currentStep === steps.length - 1 ? handleFinalSubmit : handleNext,
    onBack: handleBack,
    isNextActive: true,
    isFinalStep: currentStep === steps.length - 1,
    currentStep,
  };

  const alertDialogConfig: AlertDialogConfig = {
    setIsAlertOpen,
    isAlertOpen,
  };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <SubmitItem
        title="Criar ocorrência"
        formTitle="Informações da ocorrência"
        hasAlert={currentStep === steps.length - 1}
        hasSubmitFooter
        hasStepper
        stepperConfig={stepperConfig}
        alertDialogConfig={alertDialogConfig}
        isLoaded={isLoaded}
        isDirty={isDirty}
      >
        <FieldOccurancesSubmitForm
          errors={errors}
          control={control}
          resetField={resetField}
          watch={watch}
          isDisabled={currentStep === steps.length - 1}
        />
      </SubmitItem>
    </form>
  );
};

export default NewFieldOccurrences;
